import { styled } from "@mui/material/styles";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { FONT_FAMILY } from "@/utils/font-family";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useTranslation } from "react-i18next";
import { useStyle } from "@/components/tables/style";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { adaptMarginRight } from "@/utils/adapter";
import { useState, useMemo } from "react";
import { useIsMobile } from "@/hooks/use-responsive";
import { useTheme } from "@/contexts/theme";
import {
  TableEmptyState,
  IEmptyStateConfig,
  DEFAULT_EMPTY_STATE,
} from "@/components/tables/table-empty-state";
import { getDocumentTableHeaderLabel } from "@/pages-components/quotes/utils/document-table-header-label";

export type { IEmptyStateConfig } from "@/components/tables/table-empty-state";

interface IDocumentTableProps {
  rows?: Array<Record<string, any>>; // Optional for safety
  headers: string[];
  stickyHeader?: boolean;
  stickyFirstCol?: boolean;
  maxHeight?: any;
  columnWidths?: string[];
  withSort?: boolean;
  withSortBE?: boolean;
  onClickSortBE?: (key: string) => void;
  isDescOrderBE?: boolean;
  isHeaderSortable?: any;
  docType?: number;
  dataTour?: string;
  loading?: boolean;
  error?: boolean;
  disableSkeleton?: boolean; // do not show skeleton (e.g., during realtime/socket updates)
  /** When provided, shows the designed empty state (icon, title, description, action) when there is no data */
  emptyState?: IEmptyStateConfig;
  getRowSx?: (row: Record<string, any>) => object | undefined;
  getRowTooltip?: (row: Record<string, any>) => string | null | undefined;
}

const StyledTableCell = styled(TableCell)(() => {
  const { primaryColor } = useGomakeTheme();
  const isMobile = useIsMobile();

  return {
    borderInlineEnd: "var(--theme-table-column-divider, none)",
    [`&.${tableCellClasses.head}`]: {
      backgroundColor:
        "var(--theme-table-head-bg, var(--theme-primary-100, " +
        primaryColor(50) +
        "))",
      color:
        "var(--theme-table-head-color, var(--theme-text-primary, " +
        primaryColor(900) +
        "))",
      ...FONT_FAMILY.Lexend(500, 14),
      padding:
        "var(--theme-table-cell-padding, " +
        (isMobile ? "5px 10px" : "5px") +
        ")",
      height: "var(--theme-table-row-height, 47px)",
      minHeight: "var(--theme-table-row-height, 47px)",
      fontSize: "var(--theme-table-head-font-size, 14px)",
      textAlign: "var(--theme-table-header-align, center)",
    },
    [`&.${tableCellClasses.body}`]: {
      ...FONT_FAMILY.Lexend(500, 14),
      color:
        "var(--theme-table-body-color, var(--theme-text-primary, " +
        primaryColor(500) +
        "))",
      padding:
        "var(--theme-table-cell-padding, " +
        (isMobile ? "5px 10px" : "3px 0") +
        ")",
      height: "var(--theme-table-row-height, 47px)",
      minHeight: "var(--theme-table-row-height, 47px)",
      fontSize: "var(--theme-table-cell-font-size, 14px)",
      textAlign: "var(--theme-table-cell-align, center)",
    },
  };
});

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "var(--theme-table-row-even-bg, transparent)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--theme-table-row-odd-bg, transparent)",
  },
  position: "relative",
  "&:last-child td, &:last-child th": {
    borderBottom: "none",
  },
}));

const StyledTableContainer = styled(TableContainer)(() => {
  return {
    // Enable horizontal scrolling
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      width: "var(--theme-table-scrollbar-width, 10px)",
      height: "var(--theme-table-scrollbar-height, 10px)",
    },
    "&::-webkit-scrollbar-track": {
      background:
        "var(--theme-table-scrollbar-track, var(--theme-bg-tertiary, #f1f1f1))",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "var(--theme-table-scrollbar-thumb, #CCCDCF)",
      borderRadius: "10px",
      border: "var(--theme-table-scrollbar-thumb-border, none)",
      backgroundClip: "padding-box",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      cursor: "pointer",
    },
  };
});

export const DocumentPrimaryTable = ({
  rows = [],
  headers = [],
  stickyHeader = true,
  maxHeight,
  columnWidths,
  withSort = false,
  withSortBE = false,
  onClickSortBE,
  isDescOrderBE = false,
  isHeaderSortable,
  docType,
  dataTour,
  loading = false,
  error = false,
  disableSkeleton = false,
  emptyState,
  getRowSx,
  getRowTooltip,
}: IDocumentTableProps) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const { classes } = useStyle(maxHeight, dir);
  const { currentTheme } = useTheme();

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig) return rows;

    return [...rows].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sortConfig]);

  const isActionsColumn = (key: string) =>
    currentTheme === "hp-client" && (key === "more" || key === "actions");

  const getHeaderLabel = (key: string) =>
    getDocumentTableHeaderLabel(key, docType, t);

  const showEmptyState =
    sortedRows.length === 0 && !error && !(loading && !disableSkeleton);

  const resolvedEmptyState: IEmptyStateConfig = showEmptyState
    ? { ...DEFAULT_EMPTY_STATE, ...emptyState }
    : DEFAULT_EMPTY_STATE;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        overflow: "hidden",
        height: "100%",
        boxSizing: "border-box",
        background: "var(--theme-table-container-bg, #FFF)",
        border: "var(--theme-table-container-border, none)",
        borderRadius: "var(--theme-table-container-radius, 0)",
        boxShadow:
          "var(--theme-table-paper-shadow, 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12))",
      }}
    >
      <StyledTableContainer
        dir={dir}
        style={{
          ...classes.tableContainer,
          boxShadow:
            "var(--theme-table-container-shadow, 0px 4px 40px 0px #00000014)",
        }}
      >
        {showEmptyState && (
          <TableEmptyState
            config={resolvedEmptyState}
            wrapperStyle={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 52,
              bottom: 0,
            }}
          />
        )}
        <Table stickyHeader={stickyHeader} data-tour={dataTour} dir={dir}>
          <TableHead>
            <TableRow>
              {headers.map((key, index) => (
                <StyledTableCell
                  key={`header_${key}`}
                  align={isActionsColumn(key) ? "center" : undefined}
                  onMouseEnter={() => setHoveredColumn(key)}
                  onMouseLeave={() => setHoveredColumn(null)}
                  style={{
                    cursor: withSort || withSortBE ? "pointer" : "default",
                    userSelect: "none",
                    ...(index === 0 && stickyHeader
                      ? { position: "sticky", top: 0 }
                      : {}),
                  }}
                  onClick={() => {
                    if (withSort) handleSort(key);
                    if (withSortBE && onClickSortBE) onClickSortBE(key);
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: isActionsColumn(key)
                        ? "flex-end"
                        : "var(--theme-table-header-justify, center)",
                      alignItems: "center",
                    }}
                  >
                    {isActionsColumn(key) ? "" : getHeaderLabel(key)}
                    {withSort && sortConfig?.key === key && (
                      <IconButton
                        size="small"
                        style={{ ...adaptMarginRight(dir, 4) }}
                      >
                        {sortConfig.direction === "asc" ? (
                          <ArrowUpward sx={{ fontSize: "18px" }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: "18px" }} />
                        )}
                      </IconButton>
                    )}

                    {withSortBE && isHeaderSortable?.(docType, key) && (
                      <IconButton
                        size="small"
                        style={{
                          ...adaptMarginRight(dir, 4),
                          visibility:
                            hoveredColumn === key ? "visible" : "hidden",
                        }}
                      >
                        {!isDescOrderBE ? (
                          <ArrowUpward sx={{ fontSize: "18px" }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: "18px" }} />
                        )}
                      </IconButton>
                    )}
                  </div>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row, rowIndex) => {
                const rowSx = getRowSx?.(row);
                const rowTooltip = getRowTooltip?.(row);
                const tableRow = (
                  <StyledTableRow key={`row_${rowIndex}`} sx={rowSx}>
                    {headers.map((key, cellIndex) => {
                      const cellStyle = columnWidths
                        ? { width: columnWidths[cellIndex] }
                        : {};
                      return (
                        <StyledTableCell
                          align={isActionsColumn(key) ? "center" : undefined}
                          key={`cell_${rowIndex}_${cellIndex}`}
                          style={{
                            ...cellStyle,
                            ...(isActionsColumn(key)
                              ? { textAlign: "center" }
                              : {}),
                          }}
                        >
                          {row[key]}
                        </StyledTableCell>
                      );
                    })}
                  </StyledTableRow>
                );

                if (!rowTooltip) {
                  return tableRow;
                }

                return (
                  <Tooltip
                    key={`row_${rowIndex}`}
                    title={rowTooltip}
                    arrow
                    disableInteractive
                  >
                    {tableRow}
                  </Tooltip>
                );
              })
            ) : (
              <StyledTableRow>
                <StyledTableCell
                  align="center"
                  colSpan={headers.length}
                  style={{
                    padding: showEmptyState ? 0 : "20px",
                    border: "none",
                  }}
                >
                  {error ? (
                    <span>{t("skeleton.errorGettingData")}</span>
                  ) : loading && !disableSkeleton ? (
                    <div style={{ width: "100%" }}>
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <div
                          key={`sk-${idx}`}
                          style={{ display: "flex", gap: 8, marginBottom: 8 }}
                        >
                          {headers.map((_, cIdx) => (
                            <div key={`sk-${idx}-${cIdx}`} style={{ flex: 1 }}>
                              <Skeleton variant="rounded" height={32} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : showEmptyState /* Empty state is rendered as overlay above */ ? null : null}
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Paper>
  );
};
